/**
 * Орлогын графикийн зурах давхарга — SVG + HTML цэгүүд.
 *
 * ⚠️ ӨНГӨ БҮГД `var(--color-*)`. SVG нь CSS хувьсагчийг төрөлхөөсөө
 *    уншдаг тул харанхуй горим ямар ч JS-гүйгээр дагана.
 *
 * ⚠️ MONOTONE муруй (`lib/monotone.ts`) — тэгээс доош цухуйхгүй.
 *
 * DARK GLASS (2026-10-05): шугам зүүнээс баруун тийш 2.6s-д зурагдаж,
 * толгойд нь цагаан цэг гүйнэ; дууссаны дараа цэг ОРГИЛ дээр сууж
 * тайлбар (`tip`) гарна. Хулгана аваачихад цэг тэр өдөр рүү шилжинэ.
 *
 * ⚠️ Цэг, курсор, тайлбар нь HTML (хувиар байрлана): SVG нь
 *    `preserveAspectRatio="none"` тул `<circle>` зууван болно.
 * ⚠️ X тэнхлэгийн шошго энд БАЙХГҮЙ — `revenue-chart.tsx` дотор HTML.
 */

import type { CSSProperties, ReactNode } from 'react';
import { monotonePath, monotoneY, type Pt } from '../../lib/monotone';
import { useTween } from '../../lib/use-tween';

interface Props {
  values: number[];
  /** Өмнөх хугацаа — тасархай шугам. Хоосон бол зурахгүй. */
  compare?: number[];
  /** Хулганы доорх цэгийн индекс */
  hover: number | null;
  onHover: (i: number | null) => void;
  height: number;
  /** Оргил цэгийн индекс — зурж дууссаны дараа курсор энд сууна */
  peak: number | null;
  /** Курсорын дээрх тайлбар. Зурж дуусаагүй үед гарахгүй. */
  tip?: ReactNode;
}

const W = 1000;
const PAD_T = 14;
const PAD_B = 6;
/** Үүнээс олон цэгтэй бол (сар, өдөр) цэг бүрийг тэмдэглэхгүй — бөөгнөрнө */
const MAX_DOTS = 12;

export function RevenuePlot({ values, compare, hover, onHover, height, peak, tip }: Props) {
  const progress = useTween(1, 2600);
  const plotH = height - PAD_T - PAD_B;
  const base = PAD_T + plotH;
  // Хамгийн өндөр нь compare-ыг ч багтаана — хоёр шугам нэг масштабтай
  const max = Math.max(1, ...values, ...(compare ?? []));
  const step = values.length > 1 ? W / (values.length - 1) : 0;

  const toPts = (arr: number[]): Pt[] =>
    arr.map((v, i) => ({ x: i * step, y: PAD_T + plotH - (v / max) * plotH }));

  const pts = toPts(values);
  const line = monotonePath(pts);
  const area = pts.length ? `${line} L${W} ${base} L0 ${base} Z` : '';

  const drawn = progress >= 1;
  const headX = progress * W;
  const at = hover ?? peak;
  const cursor: Pt | null = !drawn
    ? { x: headX, y: monotoneY(pts, headX) }
    : at === null
      ? null
      : (pts[at] ?? null);

  const place = (p: Pt): CSSProperties => ({
    left: `${(p.x / W) * 100}%`,
    top: `${(p.y / height) * 100}%`,
  });
  const xPct = cursor ? (cursor.x / W) * 100 : 50;

  return (
    <div className="relative h-full w-full">
      <svg
        viewBox={`0 0 ${W} ${height}`}
        preserveAspectRatio="none"
        className="h-full w-full touch-pan-y overflow-visible"
        role="img"
        aria-label="Орлогын график"
        onPointerLeave={() => onHover(null)}
        onPointerMove={(e) => {
          const r = e.currentTarget.getBoundingClientRect();
          if (r.width === 0 || values.length < 2) return;
          const i = Math.round(((e.clientX - r.left) / r.width) * (values.length - 1));
          onHover(Math.min(values.length - 1, Math.max(0, i)));
        }}
      >
        <defs>
          <linearGradient id="rev-fill" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="var(--color-accent)" stopOpacity="0.5" />
            <stop offset="100%" stopColor="var(--color-accent)" stopOpacity="0" />
          </linearGradient>
          <linearGradient id="rev-line" x1="0" y1="0" x2="1" y2="0">
            <stop offset="0" stopColor="var(--color-accent-2)" />
            <stop offset="0.7" stopColor="var(--color-accent)" />
            <stop offset="1" stopColor="var(--color-line-end)" />
          </linearGradient>
          <clipPath id="rev-clip">
            <rect x="-10" y={-40} width={headX + 10} height={height + 80} />
          </clipPath>
        </defs>

        {/* Хэвтээ туслах шугам — тасархай, бүдэг. Босоо шугам байхгүй. */}
        {[0, 1 / 3, 2 / 3, 1].map((f) => (
          <line
            key={f}
            x1="0" x2={W}
            y1={PAD_T + plotH * f} y2={PAD_T + plotH * f}
            stroke="var(--color-hair)" strokeWidth="1" strokeDasharray="4 6"
            vectorEffect="non-scaling-stroke"
          />
        ))}

        {compare && compare.length === values.length ? (
          <path
            d={monotonePath(toPts(compare))}
            fill="none"
            stroke="var(--color-ink-3)"
            strokeWidth="1.4"
            strokeDasharray="5 4"
            strokeLinecap="round"
            vectorEffect="non-scaling-stroke"
            opacity="0.75"
          />
        ) : null}

        <g clipPath="url(#rev-clip)">
          <path d={area} fill="url(#rev-fill)" />
          {/* Гэрэлтэлт — өргөн, тунгалаг доод давхарга */}
          <path
            d={line} fill="none" stroke="var(--color-accent)" strokeOpacity="0.25"
            strokeWidth="12" strokeLinecap="round" vectorEffect="non-scaling-stroke"
          />
          <path
            d={line} fill="none" stroke="url(#rev-line)" strokeWidth="3.5"
            strokeLinejoin="round" strokeLinecap="round" vectorEffect="non-scaling-stroke"
          />
        </g>

        {cursor ? (
          <line
            x1={cursor.x} x2={cursor.x} y1={cursor.y} y2={base}
            stroke="var(--color-accent)" strokeOpacity="0.4" strokeWidth="1" strokeDasharray="3 4"
            vectorEffect="non-scaling-stroke"
          />
        ) : null}
      </svg>

      {pts.length <= MAX_DOTS
        ? pts.map((p, i) =>
            p.x <= headX + 0.5 ? (
              <span
                key={i}
                aria-hidden
                style={place(p)}
                className="pop-in pointer-events-none absolute size-[9px] -translate-1/2 rounded-pill
                  border-[2.5px] border-accent bg-bg"
              />
            ) : null,
          )
        : null}

      {cursor ? (
        <span aria-hidden style={place(cursor)} className="pointer-events-none absolute -translate-1/2">
          <span className="absolute -inset-3 animate-pulse rounded-pill bg-accent/25" />
          <span className="relative block size-3.5 rounded-pill border-[3.5px] border-accent bg-white" />
        </span>
      ) : null}

      {drawn && cursor && tip ? (
        <div
          key={at ?? -1}
          role="status"
          style={{
            ...place(cursor),
            transform: `translate(${xPct < 12 ? '-12%' : xPct > 88 ? '-88%' : '-50%'}, calc(-100% - 18px))`,
          }}
          className="pointer-events-none absolute z-10 flex min-w-[140px] flex-col items-center gap-0.5
            rounded-well border border-accent-300 bg-panel px-3.5 py-2.5 text-center shadow-pop"
        >
          {tip}
        </div>
      ) : null}
    </div>
  );
}
